import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getEventWithDetails, updateEventExtended } from '../services/eventsService';
import { getStripeConnectStatus } from '../services/stripeConnectService';
import { getStatusLabel, DB_STATUS } from '../utils/eventStatusMapping';

// Import tab components
import BasicInfoTab from '../components/EventEditor/BasicInfoTab';
import InstructorsTab from '../components/EventEditor/InstructorsTab';
import SessionsTab from '../components/EventEditor/SessionsTab';
import LearningObjectivesTab from '../components/EventEditor/LearningObjectivesTab';
import FAQsTab from '../components/EventEditor/FAQsTab';
import TestimonialsTab from '../components/EventEditor/TestimonialsTab';
import BonusesTab from '../components/EventEditor/BonusesTab';
import LandingPageTab from '../components/EventEditor/LandingPageTab';
import PresentationsTab from '../components/EventEditor/PresentationsTab';

const {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiInfo,
  FiUsers,
  FiCalendar,
  FiTarget,
  FiHelpCircle,
  FiStar,
  FiGift,
  FiLayout,
  FiAlertCircle,
  FiCheckCircle,
  FiSliders
} = FiIcons;

const TABS = [
  { id: 'basic', label: 'Basic Info', icon: FiInfo },
  { id: 'instructors', label: 'Instructors', icon: FiUsers },
  { id: 'sessions', label: 'Sessions', icon: FiCalendar },
  { id: 'objectives', label: 'Learning Objectives', icon: FiTarget },
  { id: 'faqs', label: 'FAQs', icon: FiHelpCircle },
  { id: 'testimonials', label: 'Testimonials', icon: FiStar },
  { id: 'bonuses', label: 'Bonuses', icon: FiGift },
  { id: 'presentations', label: 'Presentations', icon: FiSliders },
  { id: 'landing', label: 'Landing Page', icon: FiLayout }
];

function isUserIdRequiredError(error) {
  if (!error) return false;
  const code = error?.code ?? '';
  const message = (error?.message ?? '').toString();
  return code === 'access_denied' && message.includes('User id is required');
}

export default function EventEditor() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { profile, loading: authLoading, profileLoading, authReady } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [authProfileError, setAuthProfileError] = useState(false);
  const [stripeOnboardingCompleted, setStripeOnboardingCompleted] = useState(null);

  const loadEventData = useCallback(async () => {
    if (!profile?.id || !profile?.role) return;
    try {
      setLoading(true);
      setAuthProfileError(false);
      const { data, error } = await getEventWithDetails(eventId, {
        userId: profile.id,
        role: profile.role
      });

      if (error) {
        if (isUserIdRequiredError(error)) {
          setAuthProfileError(true);
          toast.error('Unable to load your profile. Please try again.');
          return;
        }
        console.error('[EventEditor] Error loading event:', error);
        toast.error('Failed to load event details');
        setEvent(null);
        return;
      }

      setEvent(data);
    } catch (error) {
      if (isUserIdRequiredError(error)) {
        setAuthProfileError(true);
        toast.error('Unable to load your profile. Please try again.');
        return;
      }
      console.error('[EventEditor] Error loading event:', error);
      toast.error('Failed to load event details');
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, profile?.id, profile?.role]);

  useEffect(() => {
    if (!authReady || authLoading || profileLoading || !profile?.id || !profile?.role) {
      return;
    }
    loadEventData();
  }, [eventId, authReady, authLoading, profileLoading, profile?.id, profile?.role, loadEventData]);

  // Load Stripe Connect status when event has a price (hide warning when agent has connected)
  useEffect(() => {
    if (!event || !profile?.id || !profile?.role || (profile.role !== 'agent' && profile.role !== 'admin')) {
      return;
    }
    const isPaidEvent = (event?.price ?? 0) > 0;
    if (!isPaidEvent) {
      setStripeOnboardingCompleted(true);
      return;
    }
    let cancelled = false;
    getStripeConnectStatus({ role: profile.role, userId: profile.id }).then(({ data }) => {
      if (!cancelled && data) {
        setStripeOnboardingCompleted(data.stripe_onboarding_completed === true);
      }
    });
    return () => { cancelled = true; };
  }, [event?.id, event?.price, profile?.id, profile?.role]);

  async function handleSave() {
    if (!profile?.id || !profile?.role) return;

    const publishingToPublic = event?.status === DB_STATUS.PUBLIC;
    const isPaidEvent = (event?.price ?? 0) > 0;
    const usePlatformAccount = event?.use_platform_account === true;
    if (publishingToPublic && isPaidEvent && !usePlatformAccount) {
      const { data: stripeStatus, error: stripeError } = await getStripeConnectStatus({
        role: profile.role,
        userId: profile.id
      });
      if (stripeError || !stripeStatus?.stripe_onboarding_completed) {
        toast.error('You must connect your Stripe account before publishing paid events.');
        return;
      }
    }

    try {
      setSaving(true);
      const { error } = await updateEventExtended(eventId, event, {
        userId: profile.id,
        role: profile.role
      });

      if (error) {
        throw error;
      }

      setHasUnsavedChanges(false);
      toast.success('Event saved successfully');
    } catch (error) {
      console.error('[EventEditor] Error saving event:', error);
      toast.error(error?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  }

  function handlePreview() {
    // Open landing page in new tab
    window.open(`/landing/${eventId}`, '_blank');
  }

  function handleEventChange(updates) {
    setEvent(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  }

  // Wait for auth and profile to be ready
  if (authLoading || profileLoading || !authReady) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Auth done but no valid profile (e.g. profile fetch failed)
  if (!profile?.id || !profile?.role) {
    return <Navigate to="/login" replace />;
  }

  // Still loading event data
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Auth/profile error from service (e.g. "User id is required" - defensive)
  if (authProfileError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load your profile</h2>
        <p className="text-gray-600 mb-4">Please sign in again to continue.</p>
        <button
          onClick={() => navigate('/login')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Sign in again
        </button>
      </div>
    );
  }

  // Genuine event not found / no access
  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Event not found</h2>
        <p className="text-gray-600 mb-4">The event you're looking for doesn't exist or you don't have access.</p>
        <button
          onClick={() => navigate('/dashboard/events')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Events
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/events')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {event?.title || 'Edit Event'}
                </h1>
                <p className="text-sm text-gray-500">
                  {event?.type || 'Event'} {event?.status && `– ${getStatusLabel(event.status)}`}
                </p>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-orange-600 flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
                  Unsaved changes
                </span>
              )}

              <button
                onClick={handlePreview}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
              >
                <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
                Preview
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 overflow-x-auto pb-px">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                    ${isActive
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={tab.icon} className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </div>

                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'basic' && (
              <BasicInfoTab
                event={event}
                onChange={handleEventChange}
                stripeOnboardingCompleted={stripeOnboardingCompleted}
              />
            )}

            {activeTab === 'instructors' && (
              <InstructorsTab
                eventId={eventId}
                onUpdate={loadEventData}
              />
            )}

            {activeTab === 'sessions' && (
              <SessionsTab
                eventId={eventId}
                sessions={event?.sessions || []}
                orientationSessionId={event?.orientation_session_id}
                onUpdate={loadEventData}
                eventTimezone={event?.timezone || undefined}
                eventFormat={event?.format || 'online'}
              />
            )}

            {activeTab === 'objectives' && (
              <LearningObjectivesTab
                eventId={eventId}
              />
            )}

            {activeTab === 'faqs' && (
              <FAQsTab
                eventId={eventId}
              />
            )}

            {activeTab === 'testimonials' && (
              <TestimonialsTab
                eventId={eventId}
              />
            )}

            {activeTab === 'bonuses' && (
              <BonusesTab
                eventId={eventId}
              />
            )}

            {activeTab === 'presentations' && (
              <PresentationsTab eventId={eventId} />
            )}

            {activeTab === 'landing' && (
              <LandingPageTab
                eventId={eventId}
                event={event}
                onChange={handleEventChange}
                onUpdate={loadEventData}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
